package com.milta.core.auth

import com.milta.BuildKonfig
import com.milta.feature.auth.domain.entity.OAuthProvider
import kotlinx.browser.document
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.js.json
import kotlin.time.Duration.Companion.minutes
import org.w3c.dom.HTMLScriptElement

/**
 * Web (Kotlin/JS) OAuth sign-in.
 *
 * Google: Google Identity Services (GIS) JS SDK — `google.accounts.id.prompt()` triggered from
 * inside the "Continue with Google" button's click handler (still works as a user-gesture-backed
 * prompt under FedCM; the browser itself renders the account chooser). Returns an ID token via a
 * one-shot JS callback bridged to a [CompletableDeferred]. firstName/lastName/email are read
 * directly from the (unverified client-side, re-checked server-side) ID token claims — the GIS
 * SDK doesn't surface them separately the way Android's Credential Manager does.
 *
 * Apple: not wired on web — the backend's `/auth/oauth/apple/callback` only redirects to the
 * mobile deep link scheme (`miltaapp://...`) today, which a browser tab can't follow. Left
 * unimplemented on purpose (decision 2026-08-15, see `docs/TODO.md`).
 */
class JsOAuthSignInClient : OAuthSignInClient {

    override suspend fun signIn(provider: OAuthProvider): OAuthSignInResult = when (provider) {
        OAuthProvider.GOOGLE -> signInWithGoogle()
        OAuthProvider.APPLE -> throw UnsupportedOperationException("Apple sign-in isn't wired on web yet")
    }

    private suspend fun signInWithGoogle(): OAuthSignInResult {
        ensureGsiScript()

        val deferred = CompletableDeferred<String>()
        val google: dynamic = js("window").google
        google.accounts.id.initialize(
            json(
                "client_id" to BuildKonfig.GOOGLE_WEB_CLIENT_ID,
                "callback" to { response: dynamic -> deferred.complete(response.credential as String) },
                "use_fedcm_for_prompt" to true,
            ),
        )
        // Without a moment listener, a silent refusal to display (blocked third-party storage,
        // no FedCM support, cooldown after a prior dismiss...) leaves the caller hanging for the
        // full 2-minute timeout with no signal. Fail fast instead, and log the reason Google gave.
        google.accounts.id.prompt(fun(notification: dynamic) {
            if (deferred.isCompleted) return
            val notDisplayed: Boolean = notification.isNotDisplayed()
            val skipped: Boolean = notification.isSkippedMoment()
            if (notDisplayed || skipped) {
                val reason: String? = if (notDisplayed) notification.getNotDisplayedReason() else notification.getSkippedReason()
                println("Google One Tap did not display (reason=$reason)")
                deferred.completeExceptionally(IllegalStateException("Google sign-in prompt was not displayed (reason=$reason)"))
            }
        })

        val idToken = withTimeoutOrNull(2.minutes) { deferred.await() }
            ?: throw IllegalStateException("Google sign-in timed out or was dismissed")

        val claims = decodeIdTokenClaims(idToken)
        return OAuthSignInResult(
            idToken = idToken,
            firstName = claims?.get("given_name")?.jsonPrimitive?.contentOrNull,
            lastName = claims?.get("family_name")?.jsonPrimitive?.contentOrNull,
            email = claims?.get("email")?.jsonPrimitive?.contentOrNull,
        )
    }

    private suspend fun ensureGsiScript() {
        val existing: dynamic = js("window").google
        if (existing != null && existing.accounts != null) return

        val deferred = CompletableDeferred<Unit>()
        val script = document.createElement("script") as HTMLScriptElement
        script.src = "https://accounts.google.com/gsi/client"
        script.onload = { deferred.complete(Unit) }
        document.head?.appendChild(script)
        deferred.await()
    }

    @OptIn(ExperimentalEncodingApi::class)
    private fun decodeIdTokenClaims(idToken: String): JsonObject? {
        val payload = idToken.split(".").getOrNull(1) ?: return null
        val padded = payload + "=".repeat((4 - payload.length % 4) % 4)
        return runCatching {
            Json.parseToJsonElement(Base64.UrlSafe.decode(padded).decodeToString()).jsonObject
        }.getOrNull()
    }
}
